import React, { useEffect, useState } from 'react';
import { View, Text } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import { Target, Users } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { formatCurrency } from '@/lib/utils/format';
import type { CommitmentSummary } from '@/lib/state/inverio-store';

interface FundraisingRowProps {
  commitmentSummary: CommitmentSummary | null;
  isActivelyRaising?: boolean;
  stage?: string;
  variant?: 'inline' | 'detailed' | 'bare';
}

const STAGE_LABEL: Record<string, string> = {
  'pre-seed': 'Pre-Seed',
  'seed': 'Seed',
  'series-a': 'Series A',
  'series-b': 'Series B',
  'other': 'Other',
};

function formatStage(stage?: string): string | null {
  if (!stage) return null;
  return STAGE_LABEL[stage] ?? stage;
}

export function FundraisingRow({
  commitmentSummary,
  isActivelyRaising = true,
  stage,
  variant = 'inline',
}: FundraisingRowProps) {
  const colors = useThemeColors();
  const [trackWidth, setTrackWidth] = useState(0);
  const fillSV = useSharedValue(0);

  const hasData = commitmentSummary && commitmentSummary.fundingTarget > 0;
  const filled = hasData
    ? Math.min(commitmentSummary.percentageFilled, 100)
    : 0;

  useEffect(() => {
    if (trackWidth <= 0 || !hasData) {
      fillSV.value = 0;
      return;
    }
    fillSV.value = withTiming((filled / 100) * trackWidth, {
      duration: 700,
      easing: Easing.out(Easing.cubic),
    });
  }, [trackWidth, filled, hasData]);

  const fillStyle = useAnimatedStyle(() => ({ width: fillSV.value }));

  if (!isActivelyRaising && !hasData) return null;

  const stageLabel = formatStage(stage);
  const totalInvestors = hasData
    ? commitmentSummary.verifiedInvestorCount + commitmentSummary.unverifiedInvestorCount
    : 0;

  if (variant === 'inline') {
    const summary = hasData
      ? `${formatCurrency(commitmentSummary.totalCommitted)} / ${formatCurrency(commitmentSummary.fundingTarget)}`
      : 'Raising';

    return (
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingVertical: 6,
        }}
      >
        <Text
          style={{ color: colors.text.tertiary, fontSize: 11, fontWeight: '500' }}
          numberOfLines={1}
        >
          Fundraising
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', flexShrink: 1, marginLeft: 8 }}>
          {stageLabel && (
            <>
              <Text
                style={{ color: colors.text.secondary, fontSize: 11, fontWeight: '600' }}
                numberOfLines={1}
              >
                {stageLabel}
              </Text>
              <Text style={{ color: colors.text.tertiary, fontSize: 11, marginHorizontal: 6 }}>·</Text>
            </>
          )}
          <Text
            style={{ color: colors.text.secondary, fontSize: 11, fontWeight: '600' }}
            numberOfLines={1}
          >
            {summary}
          </Text>
          {hasData && (
            <Text
              style={{ color: colors.text.tertiary, fontSize: 11, marginLeft: 6 }}
              numberOfLines={1}
            >
              ({Math.round(filled)}%)
            </Text>
          )}
        </View>
      </View>
    );
  }

  const isBare = variant === 'bare';
  const containerStyle = isBare
    ? {}
    : {
        backgroundColor: colors.surface.secondary,
        borderRadius: 12,
        padding: 14,
        borderWidth: 1,
        borderColor: colors.border.light,
      };

  return (
    <View style={containerStyle}>
      {!isBare && (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Target size={14} color={colors.primary[500]} />
            <Text
              style={{
                color: colors.text.tertiary,
                fontSize: 10,
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
              }}
            >
              Fundraising
            </Text>
          </View>
          {stageLabel && (
            <View
              style={{
                backgroundColor: colors.primary[50],
                paddingHorizontal: 8,
                paddingVertical: 3,
                borderRadius: 999,
              }}
            >
              <Text
                style={{
                  color: colors.primary[600],
                  fontSize: 10,
                  fontWeight: '700',
                  letterSpacing: 0.3,
                }}
                numberOfLines={1}
              >
                {stageLabel}
              </Text>
            </View>
          )}
        </View>
      )}

      {hasData ? (
        <>
          <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text
              style={{ color: colors.text.primary, fontSize: 18, fontWeight: '700', letterSpacing: -0.3 }}
              numberOfLines={1}
            >
              {formatCurrency(commitmentSummary.totalCommitted)}
            </Text>
            <Text
              style={{ color: colors.text.tertiary, fontSize: 12, fontWeight: '500' }}
              numberOfLines={1}
            >
              of {formatCurrency(commitmentSummary.fundingTarget)} ({Math.round(filled)}%)
            </Text>
          </View>

          <View
            onLayout={(e) => setTrackWidth(e.nativeEvent.layout.width)}
            style={{
              height: 6,
              backgroundColor: colors.border.light,
              borderRadius: 3,
              overflow: 'hidden',
              marginBottom: 10,
            }}
          >
            <Animated.View
              style={[
                {
                  height: '100%',
                  backgroundColor: colors.primary[500],
                  borderRadius: 3,
                },
                fillStyle,
              ]}
            />
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              <Users size={11} color={colors.text.tertiary} />
              <Text style={{ color: colors.text.tertiary, fontSize: 11 }} numberOfLines={1}>
                {totalInvestors} investor{totalInvestors === 1 ? '' : 's'}
              </Text>
            </View>
            {commitmentSummary.hardCommitCount > 0 && (
              <Text style={{ color: colors.success, fontSize: 11, fontWeight: '600' }} numberOfLines={1}>
                {commitmentSummary.hardCommitCount} hard
              </Text>
            )}
            {commitmentSummary.softCommitCount > 0 && (
              <Text style={{ color: colors.warning, fontSize: 11, fontWeight: '600' }} numberOfLines={1}>
                {commitmentSummary.softCommitCount} soft
              </Text>
            )}
          </View>
        </>
      ) : (
        <Text style={{ color: colors.text.secondary, fontSize: 12 }} numberOfLines={2}>
          Actively raising — round details not yet posted.
        </Text>
      )}
    </View>
  );
}
